import {useEffect,useRef,useState} from 'react';
import {collection,doc,getDoc,onSnapshot,runTransaction} from 'firebase/firestore';
import {db} from '../../Security/firebase-config';
import {useAuth} from '../../context/AuthContext';
import {useTeam} from '../../context/TeamContext';
import {useLocation} from 'react-router-dom';
import {getRequestedTeamId} from '../../utils/teamRouteTarget';
import {ensureEventSetupIds} from '../../utils/eventSetupStore';
import {canEditMatchPrep,validateMatchEvent} from '../match-prep/matchPrepModel';
import {homeworkPayload,memberIds} from '../homework/homeworkModel';
import {plannerEventKey} from '../planning/plannerEvents';
import subscribePlannerNotes from '../planning/subscribePlannerNotes';
import {prepPayload} from './prepModel';

export function usePrepTeam(input,teamIdOverride) {
  const {userData}=useAuth(),context=useTeam(),location=useLocation();
  const teamId=teamIdOverride||getRequestedTeamId(location.search)||context.teamId;
  const identity=JSON.stringify([userData?.uid,teamId]);
  const [state,setState]=useState({identity,loading:true,team:null,roster:[],maps:[],notes:[],error:''});
  const [attempt,setAttempt]=useState(0);
  useEffect(()=>{
    let alive=true,version=0,repair=false;
    setState({identity,loading:true,team:null,roster:[],maps:[],notes:[],error:''});
    const update=patch=>{if(alive)setState(s=>({...s,...patch}));};
    if(!teamId||!userData?.uid){update({loading:false,error:'Select a team to prepare events.'});return()=>{alive=false;};}
    const stop=onSnapshot(doc(db,'teams',teamId),async snap=>{
      if(!alive)return;
      const team=snap.exists()?snap.data():null;
      if(!team||!memberIds(team).includes(userData.uid)){update({loading:false,team:null,error:'You are no longer a member of this team.'});return;}
      update({team,loading:false,error:''});
      if(!repair && (team.events || []).some(e=>e && typeof e==='object' && (typeof e.eventId!=='string'||!e.eventId.trim()))) {
        repair=true;ensureEventSetupIds({teamId,scope:'team'}).catch(()=>update({error:'Some older events need ID repair. Retry loading to include them.'}));
      }
      const current=++version;
      const roster=await Promise.all(memberIds(team).map(async uid=>{
        const profile=await getDoc(doc(db,'uids',uid)).catch(()=>null);
        const embedded=team.members.find(m=>m?.uid===uid);
        return {uid,username:profile?.data()?.username||team.memberUsernames?.[uid]||embedded?.username||uid,role:team.teamRoles?.[uid]||embedded?.role||'Player'};
      }));
      if(current===version)update({roster});
    },e=>update({loading:false,team:null,error:e.message||'Could not load team events.'}));
    const stopNotes=input?subscribePlannerNotes(teamId,maps=>update({maps}),notes=>update({notes})):()=>{};
    return()=>{alive=false;stop();stopNotes();};
  },[identity,teamId,userData?.uid,input,attempt]);
  return {...(state.identity===identity?state:{loading:true,team:null,roster:[],maps:[],notes:[],error:''}),identity,teamId,actor:{uid:userData?.uid,username:userData?.username||'Member'},canEdit:canEditMatchPrep(state.identity===identity?state.team:null,userData?.uid),retry:()=>setAttempt(a=>a+1)};
}

export function usePrepRecord(model,event) {
  const key=event?plannerEventKey(event):'';
  const identity=JSON.stringify([model.identity,key]);
  const scope=useRef(identity);scope.current=identity;
  const busy=useRef(false);
  const [state,setState]=useState({identity,loading:true,record:null,error:''});
  const [saving,setSaving]=useState(false),[attempt,setAttempt]=useState(0);
  useEffect(()=>{
    let alive=true;
    setState({identity,loading:!!key,record:null,error:''});
    if(!key||!model.teamId)return()=>{alive=false;};
    const stop=onSnapshot(doc(db,'teams',model.teamId,'eventPreps',key),snap=>{
      if(alive)setState({identity,loading:false,record:snap.exists()?snap.data():null,error:''});
    },error=>{if(alive)setState({identity,loading:false,record:null,error:error.code==='permission-denied'?'Prep data is unavailable. Check team access and deploy the new prep database rules.':error.message});});
    return()=>{alive=false;stop();};
  },[identity,key,model.teamId,attempt]);
  const data=state.identity===identity?state:{loading:true,record:null,error:''};
  async function save(draft,revision,assignment=null,publish=false) {
    if(busy.current)return null;
    busy.current=true;setSaving(true);
    try {
      const record=await runTransaction(db,async tx=>{
        const ref=doc(db,'teams',model.teamId,'eventPreps',key);
        const team=await tx.get(doc(db,'teams',model.teamId)),saved=await tx.get(ref);
        if(!canEditMatchPrep(team.data(),model.actor.uid))throw new Error('Only the team owner or preparation staff can edit prep.');
        validateMatchEvent((team.data().events||[]).map(e=>({...e,calendar:e.calendar||'team'})),event);
        const previous=saved.exists()?saved.data():null;
        if((previous?.revision||null)!==revision)throw new Error('Another member changed this prep. Your draft is preserved; reload the saved version before saving.');
        let content=draft;
        const tasks=[];
        if(assignment) {
          const recipients=[...new Set(assignment.recipients||[])];
          if(assignment.scope!=='team'&&!recipients.length)throw new Error("Choose at least one player for Drills & Exercises.");
          const selected=assignment.scope==='team'?[null]:recipients;
          for(const uid of selected) {
            const taskRef=doc(collection(db,'teams',model.teamId,'homework'));
            const task=homeworkPayload({...assignment,scope:uid?'individual':'team',assigneeUid:uid},model.actor,team.data());
            tasks.push({ref:taskRef,task});
          }
          content={...draft,assignments:[...draft.assignments,...tasks.map(({ref,task})=>({id:ref.id,title:task.title,description:task.description,quantity:task.quantity,unit:task.unit,dueDate:task.dueDate,resourceUrl:task.resourceUrl,scope:task.scope,assigneeUid:task.assigneeUid,username:model.roster.find(p=>p.uid===task.assigneeUid)?.username||'All team members',visible:true}))]};
        }
        const next=prepPayload(content,event,model.actor,previous);
        if(publish){next.publishedAt=previous?.publishedAt || new Date().toISOString();next.publishedByUid=model.actor.uid;}
        tasks.forEach(({ref,task})=>tx.set(ref,task));tx.set(ref,next);
        return next;
      });
      if(scope.current!==identity)return null;
      setState({identity,record,loading:false,error:''});
      return {ok:true,record};
    } catch(error){return scope.current===identity?{ok:false,message:error.code==='permission-denied'?'Prep was not saved. Check membership, preparation permissions, and the new database rules. Your draft is preserved.':error.message}:null;}
    finally{busy.current=false;setSaving(false);}
  }
  async function loadLegacy() {
    const [session,brief]=await Promise.all(['sessionPlans','matchPreps'].map(path=>getDoc(doc(db,'teams',model.teamId,path,key))));
    return {session:session.data()||event.sessionPlan||{},brief:brief.data()||{}};
  }
  return {...data,saving,save,loadLegacy,retry:()=>setAttempt(a=>a+1)};
}
